import { useMemo } from "react";
import { arrayOf, bool, func, number, object}  from "prop-types";
import { observer } from "mobx-react";
import { useStores } from "@hooks";
import { TEAM_ROLES, PROJECT_VISIBILITY } from "@utils";
import { useTranslation } from "react-i18next";
import { Avatar, TableCell, TableRow, Tag } from "@components";
import { Grid, useTheme } from "@material-ui/core";
import { McpSsoStatus } from "./components/McpSsoStatus";
import { MemberMenu } from "./components/MemberMenu";
import useStyle from "./UserRow.style";

export const UserRow = observer(({
   user,
   mcpSsoAccess,
   reloadData,
   totalAdmins,
   ...memberMenuProps
 }) => {
  const {
    id,
    uuid,
    memberId,
    fullname,
    avatar,
    avatarColor,
    admin,
    teamRole,
    teamRoleId,
    email,
    professionalLevelId,
    userTechnicalStack,
    status,
    projectsVisibility,
  } = user;

  const classes = useStyle();
  const { t } = useTranslation();
  const { userStore } = useStores();
  const theme = useTheme();
  const { isManager, isAdmin, data } = userStore;

  const visibilityDisplay = useMemo(() => {
    const value = projectsVisibility || "all";
    if (value === PROJECT_VISIBILITY.ASSIGNED) {
      return {
        text: t("views.team.projects_visibility.assigned"),
        color: theme.palette.warning.main,
      };
    }
    return {
      text: t("views.team.projects_visibility.all"),
      color: theme.palette.success.main,
    };
  }, [ projectsVisibility ]);

  return (
    <TableRow>
      <TableCell big width={300} flexible>
        <Grid item container wrap="nowrap" alignItems="center">
          {
            <Avatar
              fullname={fullname}
              avatar={avatar}
              color={avatarColor}
              size={40}
            />
          }
          <Grid
            item container
            direction="column"
            className="ml-2"
            style={{width: "calc(100% - 48px)"}}
          >
            <Grid item container>
              <p className={classes.name}>{fullname}</p>
              {/*{admin && (*/}
              {/*  <Tag*/}
              {/*    text={t("views.team.admin")}*/}
              {/*    color={theme.props.adminColor}*/}
              {/*  />*/}
              {/*)}*/}
            </Grid>
            <p className={classes.secondary}>{email}</p>
          </Grid>
        </Grid>
      </TableCell>
      <TableCell big width={160}>
        {teamRole && <Tag text={teamRole}/>}
      </TableCell>
      <TableCell big width={180}>
        <Tag {...visibilityDisplay} />
      </TableCell>
      <TableCell className={classes[`status${status}`]} big width={80}>
        {t(`views.team.status${status}`)}
      </TableCell>
      {
        isAdmin &&
        <TableCell big width={150}>
          <McpSsoStatus access={mcpSsoAccess}/>
        </TableCell>
      }
      <TableCell className={classes.menuCell} big width={34}>
        {
          (isAdmin || (isManager && teamRoleId !== TEAM_ROLES.ADMIN) || email === data.email) &&
          <MemberMenu
            userId={id}
            userUuid={uuid}
            memberId={memberId}
            email={email}
            name={fullname}
            professionalLevel={professionalLevelId}
            technicalStack={userTechnicalStack?.map(s => s.stackTypeId)}
            teamRole={teamRoleId}
            projectsVisibility={projectsVisibility}
            isAdmin={admin || teamRoleId === TEAM_ROLES.ADMIN}
            reloadData={reloadData}
            totalAdmins={totalAdmins}
            mcpSsoAccess={mcpSsoAccess}
            {...memberMenuProps}
          />
        }
      </TableCell>
    </TableRow>
  );
});

UserRow.propTypes = {
  user: object.isRequired,
  mcpSsoAccess: object,
  allowRoleChange: bool,
  reloadData: func.isRequired,
  totalAdmins: number.isRequired,
  professionalLevels: arrayOf(object),
  stackTypes: arrayOf(object),
  teamRoles: arrayOf(object),
};
